import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { route } from 'preact-router';
import { t } from '../i18n.js';
import { api, authState } from '../state/auth.js';
import { useAuth } from '../hooks/useAuth.js';
import Button from '../components/shared/Button.jsx';
import StatusBadge from '../components/shared/StatusBadge.jsx';
import SearchBar from '../components/shared/SearchBar.jsx';
import Filters, { MultiFilterSelect } from '../components/shared/Filters.jsx';
import Modal from '../components/shared/Modal.jsx';
import { parseServerDate } from '../utils/dates.js';
import { memberLabel, AwayNote } from '../utils/away.js';
import AiAssist from '../components/shared/AiAssist.jsx';

const STATUSES = ['open', 'in_progress', 'wacht_op_input', 'afgerond', 'geannuleerd'];

function SkeletonLine({ width = '100%', height = '16px', style = '' }) {
  return <div class="skeleton" style={`width:${width};height:${height};${style}`} />;
}

function TasksSkeleton() {
  return (
    <div>
      <div class="page-header">
        <SkeletonLine width="120px" height="28px" />
        <SkeletonLine width="120px" height="36px" />
      </div>
      <div class="card mt-4">
        {[0,1,2,3,4,5].map(i => (
          <div key={i} style="display:flex;align-items:center;gap:12px;padding:12px 16px;border-bottom:1px solid var(--color-border,#e5e7eb)">
            <SkeletonLine width="40%" height="14px" />
            <SkeletonLine width="20%" height="14px" />
            <SkeletonLine width="80px" height="22px" style="border-radius:12px" />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function TasksView() {
  const auth = useAuth();
  const currentUserId = authState.value.userId;

  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState([]);
  const [ownerFilter, setOwnerFilter] = useState([]);
  const [seasons, setSeasons]           = useState([]);
  const [seasonFilter, setSeasonFilter] = useState('');

  // New task modal
  const [newTaskOpen, setNewTaskOpen] = useState(false);
  const [newForm, setNewForm] = useState({ title: '', description: '', owner_id: '', deadline: '', start_date: '', season_id: '' });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  const canCreate = auth.hasPermission('tasks', 'create');
  const isAdmin = auth.hasPermission('settings', 'update');

  const fetchTasks = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (statusFilter.length > 0) params.set('status', statusFilter.join(','));
    if (ownerFilter.length > 0) {
      params.set('ownerIds', ownerFilter.join(','));
    } else {
      params.set('myTasks', '1');
    }
    if (search) params.set('search', search);
    if (seasonFilter) params.set('season_id', seasonFilter);
    const q = params.toString();
    api(`/api/tasks${q ? `?${q}` : ''}`)
      .then(setTasks)
      .catch(() => setError(t('errors.generic')))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    api('/api/users/members').then(setUsers).catch(() => setUsers([]));
    api('/api/seasons').then(data => {
      setSeasons(Array.isArray(data) ? data : []);
      const active = data.find(s => s.is_active);
      if (active) setNewForm(f => ({ ...f, season_id: String(active.id) }));
    }).catch(() => {});
  }, []);

  useEffect(() => { fetchTasks(); }, [statusFilter, ownerFilter, search, seasonFilter]);

  const openNewTask = () => {
    const activeSeason = seasons.find(s => s.is_active);
    setNewForm({ title: '', description: '', owner_id: String(currentUserId), deadline: '', start_date: '', season_id: activeSeason ? String(activeSeason.id) : '' });
    setCreateError(null);
    setNewTaskOpen(true);
  };

  const handleCreate = async () => {
    if (!newForm.title.trim()) { setCreateError(t('task.taskTitle') + ' is verplicht'); return; }
    setCreating(true);
    setCreateError(null);
    try {
      const task = await api('/api/tasks', {
        method: 'POST',
        body: JSON.stringify({
          title: newForm.title,
          description: newForm.description || null,
          ownerId: newForm.owner_id ? parseInt(newForm.owner_id) : null,
          deadline: newForm.deadline || null,
          startDate: newForm.start_date || null,
          seasonId: newForm.season_id ? parseInt(newForm.season_id) : null,
        }),
      });
      setNewTaskOpen(false);
      route(`/tasks/${task.id}`);
    } catch {
      setCreateError(t('errors.generic'));
    } finally {
      setCreating(false);
    }
  };

  const formatDate = (val) => val ? parseServerDate(val).toLocaleDateString() : null;

  const statusOptions = STATUSES.map(s => ({ value: s, label: t(`task.statuses.${s}`) }));

  const ownerOptions = users.map(u => ({ value: String(u.id), label: u.display_name || u.email }));

  return (
    <div>
      <div class="page-header">
        <h1 class="page-title">{t('task.title')}</h1>
        {canCreate && (
          <Button variant="primary" onClick={openNewTask}>
            {t('task.newTask')}
          </Button>
        )}
      </div>

      <Filters>
        <SearchBar value={search} onSearch={setSearch} placeholder={t('common.search')} />
        {/* Taken van anderen: alleen voor clubbeheerders (de server negeert het filter voor anderen) */}
        {isAdmin && (
          <MultiFilterSelect
            label={ownerFilter.length === 0 ? 'Mijn taken' : 'Eigenaar'}
            values={ownerFilter}
            options={ownerOptions}
            onChange={setOwnerFilter}
          />
        )}
        <MultiFilterSelect
          label={t('common.status')}
          values={statusFilter}
          options={statusOptions}
          onChange={setStatusFilter}
        />
        {seasons.length > 0 && (
          <select
            class="form-input"
            value={seasonFilter}
            onChange={e => setSeasonFilter(e.target.value)}
            style="height:36px;font-size:13px;padding:0 10px;min-width:160px"
          >
            <option value="">Alle seizoenen</option>
            {seasons.map(s => (
              <option key={s.id} value={s.id}>{s.name}{s.is_active ? ' ✓' : ''}</option>
            ))}
          </select>
        )}
      </Filters>

      {loading && (
        <div class="card mt-4">
          {[0,1,2,3,4,5].map(i => (
            <div key={i} style="display:flex;align-items:center;gap:12px;padding:12px 16px;border-bottom:1px solid var(--color-border,#e5e7eb)">
              <div class="skeleton" style="width:40%;height:14px" />
              <div class="skeleton" style="width:20%;height:14px" />
              <div class="skeleton" style="width:80px;height:22px;border-radius:12px" />
            </div>
          ))}
        </div>
      )}

      {!loading && error && <div class="alert alert-error mt-4">{error}</div>}

      {!loading && !error && tasks.length === 0 && (
        <div class="text-center text-muted" style="padding:60px">
          {t('task.noTasks')}
        </div>
      )}

      {!loading && !error && tasks.length > 0 && (
        <div class="card mt-4" style="overflow:hidden">
          {/* Header row */}
          <div style="display:grid;grid-template-columns:1fr 160px 100px 90px;gap:12px;padding:10px 16px;background:var(--color-surface-alt,#f9fafb);border-bottom:1px solid var(--color-border,#e5e7eb);font-size:12px;font-weight:600;color:var(--color-text-muted);text-transform:uppercase;letter-spacing:.04em">
            <span>{t('task.taskTitle')}</span>
            <span>{t('task.owner')}</span>
            <span>{t('task.deadline')}</span>
            <span>{t('task.status')}</span>
          </div>

          {tasks.map((task) => {
            const isOwn = task.owner_id === currentUserId;
            return (
              <div
                key={task.id}
                style="display:grid;grid-template-columns:1fr 160px 100px 90px;gap:12px;padding:12px 16px;border-bottom:1px solid var(--color-border,#e5e7eb);align-items:center;cursor:pointer;transition:background .15s"
                class="list-item-row"
                onClick={() => route(`/tasks/${task.id}`)}
              >
                <div style="min-width:0">
                  <div style="font-size:14px;font-weight:500;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">
                    {task.title}
                  </div>
                  {task.process_title && (
                    <div class="text-muted" style="font-size:11px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">
                      {task.process_title}
                    </div>
                  )}
                </div>

                <div style="display:flex;align-items:center;gap:6px;font-size:13px">
                  {isOwn ? (
                    <span style="font-weight:600;color:var(--color-primary)">{task.owner_name || '—'}</span>
                  ) : (
                    <span class="text-muted">{task.owner_name || <em style="opacity:.5">Niet toegewezen</em>}</span>
                  )}
                </div>

                <div style="font-size:13px;color:var(--color-text-muted)">
                  {formatDate(task.deadline) || '—'}
                </div>

                <StatusBadge status={task.status} type="task" />
              </div>
            );
          })}
        </div>
      )}

      {/* New task modal */}
      <Modal isOpen={newTaskOpen} title={t('task.newTask')} onClose={() => setNewTaskOpen(false)}>
        <div class="form-group">
          <label class="form-label">{t('task.taskTitle')} *</label>
          <input
            class="form-input"
            type="text"
            value={newForm.title}
            onInput={(e) => setNewForm(f => ({ ...f, title: e.target.value }))}
            placeholder={t('task.taskTitle')}
            autoFocus
          />
        </div>
        <div class="form-group">
          <label class="form-label">{t('task.description')}</label>
          <textarea
            class="form-textarea"
            rows={3}
            value={newForm.description}
            onInput={(e) => setNewForm(f => ({ ...f, description: e.target.value }))}
          />
          <AiAssist kind="task_description" value={newForm.description} context={{ title: newForm.title }} onUse={text => setNewForm(f => ({ ...f, description: text }))} />
        </div>
        <div class="form-group">
          <label class="form-label">{t('task.owner')}</label>
          <select
            class="form-select"
            value={newForm.owner_id}
            onChange={(e) => setNewForm(f => ({ ...f, owner_id: e.target.value }))}
          >
            <option value="">— Niet toegewezen</option>
            {users.map(u => (
              <option key={u.id} value={String(u.id)}>{memberLabel(u)}</option>
            ))}
          </select>
          <AwayNote users={users} userId={newForm.owner_id} />
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px">
          <div class="form-group">
            <label class="form-label">Startdatum</label>
            <input
              class="form-input"
              type="date"
              value={newForm.start_date}
              onInput={(e) => setNewForm(f => ({ ...f, start_date: e.target.value }))}
            />
          </div>
          <div class="form-group">
            <label class="form-label">{t('task.deadline')}</label>
            <input
              class="form-input"
              type="date"
              value={newForm.deadline}
              onInput={(e) => setNewForm(f => ({ ...f, deadline: e.target.value }))}
            />
          </div>
        </div>
        {seasons.length > 0 && (
          <div class="form-group">
            <label class="form-label">Seizoen</label>
            <select
              class="form-select"
              value={newForm.season_id}
              onChange={e => setNewForm(f => ({ ...f, season_id: e.target.value }))}
            >
              <option value="">Geen seizoen</option>
              {seasons.map(s => (
                <option key={s.id} value={String(s.id)}>{s.name}{s.is_active ? ' ✓' : ''}</option>
              ))}
            </select>
          </div>
        )}
        {createError && <div class="alert alert-error mb-3">{createError}</div>}
        <div class="flex gap-2 justify-end mt-4">
          <Button variant="secondary" onClick={() => setNewTaskOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="primary" onClick={handleCreate} disabled={creating}>
            {creating ? t('common.loading') : t('common.create')}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
